import React from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { getPost, blogPosts } from "@/data/blogPosts";
import useSEO from "@/hooks/useSEO";
import { SITE_URL } from "@/lib/siteUrl";
import Breadcrumbs from "@/components/Breadcrumbs";
import { Clock, Calendar } from "lucide-react";

const BlogPostPage = () => {
  const { slug } = useParams();
  const post = getPost(slug);

  useSEO({
    title: post ? `${post.title} | Ecowallet` : "Post not found | Ecowallet",
    description: post?.description,
    ogImage: post?.hero,
    ogType: "article",
    noindex: !post,
    jsonLd: post ? {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      headline: post.title,
      description: post.description,
      image: post.hero,
      datePublished: post.date,
      dateModified: post.date,
      author: { "@type": "Organization", name: "Ecowallet" },
      publisher: {
        "@type": "Organization",
        name: "Ecowallet",
        logo: { "@type": "ImageObject", url: SITE_URL + "/logo512.png" },
      },
      mainEntityOfPage: SITE_URL + "/blog/" + (post?.slug || slug),
    } : null,
  });

  if (!post) return <Navigate to="/404" replace />;

  const relatedPosts = (post.related || []).map(getPost).filter(Boolean);

  return (
    <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-24">
      <Breadcrumbs items={[{ label: "Blog", to: "/blog" }, { label: post.title }]} />

      <div className="mt-6 text-[11px] uppercase tracking-widest text-primary font-semibold">{post.cluster}</div>
      <h1 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight tracking-tight">{post.title}</h1>
      <div className="mt-4 flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
        <span className="flex items-center gap-1"><Calendar className="h-3 w-3" /> {new Date(post.date).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</span>
        <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {post.read} read</span>
      </div>

      <div className="mt-8 rounded-3xl overflow-hidden aspect-[16/9] bg-secondary">
        <img src={post.hero} alt="" className="h-full w-full object-cover" />
      </div>

      <div className="mt-8 text-lg leading-relaxed text-foreground/85 italic border-l-4 border-primary/40 pl-4">
        {post.excerpt}
      </div>

      <div className="mt-10 space-y-8 leading-relaxed text-foreground/90">
        {post.body.map((s, i) => (
          <section key={i}>
            <h2 className="text-2xl font-bold tracking-tight">{s.h}</h2>
            <p className="mt-3">{s.p}</p>
          </section>
        ))}
      </div>

      {relatedPosts.length > 0 && (
        <div className="mt-16 pt-8 border-t border-border">
          <h3 className="text-xl font-bold">Continue reading</h3>
          <div className="mt-4 grid sm:grid-cols-3 gap-3">
            {relatedPosts.map((r) => (
              <Link key={r.slug} to={`/blog/${r.slug}`} className="card-premium p-4 hover:-translate-y-1 transition-all">
                <div className="text-[10px] uppercase tracking-widest text-primary font-semibold">{r.cluster}</div>
                <div className="mt-1 font-semibold text-sm leading-snug">{r.title}</div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </article>
  );
};

export default BlogPostPage;
